import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';
import { getCorrelationId } from './correlationId';
import { isFeatureEnabled, onFlagsChanged } from './featureFlags';

/**
 * Application Insights telemetry wrapper for the Sign Later frontend.
 * Initializes the App Insights SDK (gated by the enableTelemetry feature
 * flag), tags every telemetry item with correlationId/page/source, scrubs
 * sensitive properties, and exposes trackEvent/trackException/
 * trackApiFailure/trackTrace helpers for the rest of the app to call.
 */

// Defense in depth: strip these regardless of call-site discipline.
const SENSITIVE_KEY_PATTERN =
  /password|authtoken|graphapitoken|authorization|x-ms-meta-clientemail|x-ms-meta-clientname|secret|token/i;

export type TelemetryPrimitive = string | number | boolean;
export type TelemetryProperties = Record<string, TelemetryPrimitive>;

export interface ApiFailureDetails {
  endpoint: string;
  method?: string;
  status?: number;
  errorCategory: string;
  [key: string]: unknown;
}

const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '';

// Identifies which app emitted a given telemetry item.
const TELEMETRY_SOURCE = 'signlater-frontend';

let appInsights: ApplicationInsights | null = null;

// correlationId is a UUID (dashes included) in the common case, so stripping
// non-hex characters lines it up with the 32-char lowercase hex format
// Application Insights expects for ai.operation.id.
const toOperationId = (id: string): string => {
  const hex = id.toLowerCase().replace(/[^0-9a-f]/g, '');
  return hex.length >= 32 ? hex.slice(0, 32) : hex.padEnd(32, '0');
};

// Attaches correlationId/page to every telemetry item (auto-collected
// dependencies/page views AND manual trackEvent/trackTrace/trackException
// calls below, which also pass through telemetry initializers), and
// overrides the SDK's own per-page-view Operation ID with correlationId so
// the End-to-end transaction view groups telemetry the same way
// correlationId already does.
/**
 * Telemetry initializer registered with the App Insights SDK. Runs for
 * every telemetry item, both auto-collected (dependencies/page views) and
 * manually tracked (trackEvent/trackTrace/trackException below).
 * @param item the telemetry item about to be sent
 * @returns true to allow the item through to App Insights
 */
const enrichTelemetry = (item: ITelemetryItem): boolean => {
  const correlationId = getCorrelationId();
  const baseData = item.baseData as { properties?: Record<string, unknown> } | undefined;
  if (baseData) {
    baseData.properties = {
      ...baseData.properties,
      correlationId,
      page: window.location.pathname,
      source: TELEMETRY_SOURCE,
    };
  }
  item.tags = { ...item.tags, 'ai.operation.id': toOperationId(correlationId) };
  return true;
};

if (connectionString) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
    },
  });
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(enrichTelemetry);

  const applyAppInsightsFlag = (): void => {
    isFeatureEnabled('enableTelemetry', true).then((enabled) => {
      console.log('[FeatureFlag] enableTelemetry:', enabled);

      if (appInsights) {
        appInsights.config.disableTelemetry = !enabled;
      }
    });
  };

  applyAppInsightsFlag();
  onFlagsChanged(applyAppInsightsFlag);
}

const isPrimitive = (value: unknown): value is TelemetryPrimitive =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const scrub = (properties?: Record<string, unknown>): TelemetryProperties => {
  const safe: TelemetryProperties = {};
  if (!properties) return safe;
  Object.keys(properties).forEach((key) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) return;
    const value = properties[key];
    if (!isPrimitive(value)) return;
    safe[key] = value;
  });
  return safe;
};

const enrich = (properties?: Record<string, unknown>): TelemetryProperties => ({
  ...scrub(properties),
  correlationId: getCorrelationId(),
  page: window.location.pathname,
  source: TELEMETRY_SOURCE,
});

/**
 * Reports an exception to Application Insights.
 * @param error the exception to report
 * @param context extra properties to attach, scrubbed and enriched before sending
 */
export const trackException = (
  error: Error,
  context?: Record<string, unknown>
): void => {
  if (!appInsights) return;
  appInsights.trackException({
    exception: error,
    properties: enrich(context),
  });
};

/**
 * Reports a failed API call as a warning trace.
 * @param details endpoint/method/status/errorCategory plus any extra context
 */
export const trackApiFailure = (details: ApiFailureDetails): void => {
  if (!appInsights) return;
  const { endpoint, method, status, errorCategory, ...rest } = details;
  appInsights.trackTrace(
    { message: 'ApiFailure', severityLevel: SeverityLevel.Warning },
    enrich({
      endpoint,
      method: method || '',
      status: status ?? 0,
      errorCategory,
      ...rest,
    })
  );
};

/**
 * Reports a named application event as an information trace.
 * @param name the event name
 * @param properties extra properties to attach, scrubbed and enriched before sending
 */
export const trackEvent = (name: string, properties?: Record<string, unknown>): void => {
  if (!appInsights) return;
  appInsights.trackTrace(
    { message: name, severityLevel: SeverityLevel.Information },
    enrich(properties)
  );
};

/**
 * Reports a free-form trace message at the given severity level.
 * @param message the trace message
 * @param severityLevel defaults to Information
 * @param properties extra properties to attach, scrubbed and enriched before sending
 */
export const trackTrace = (
  message: string,
  severityLevel: SeverityLevel = SeverityLevel.Information,
  properties?: Record<string, unknown>
): void => {
  if (!appInsights) return;
  appInsights.trackTrace({ message, severityLevel }, enrich(properties));
};
